import { AppState } from '../types';
import { BomWireRow, buildBom, buildCutList } from './reportData';

type Cell = string | number | undefined;

/** Escapes a single CSV field per RFC 4180 (quote when it holds a comma, quote or newline). */
function escapeField(value: Cell): string {
  const text = value === undefined ? '' : String(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/** Serialises a grid of cells to CSV text with CRLF line endings (RFC 4180). */
export function toCsv(rows: Cell[][]): string {
  return rows.map(row => row.map(escapeField).join(',')).join('\r\n');
}

/** Human-readable one-line description of a wire spec, e.g. "0.75 mm² FLY #ff0000". */
function wireDescription(row: BomWireRow): string {
  return [
    row.gauge !== undefined ? `${row.gauge} mm²` : undefined,
    row.wireType,
    row.color
  ]
    .filter(Boolean)
    .join(' ');
}

/** Builds the wire cut list as CSV: one row per wire with resolved endpoints and length. */
export function buildCutListCsv(state: AppState): string {
  const header = [
    '#', 'Wire', 'From connector', 'From pin', 'To connector', 'To pin',
    'Colour', 'Stripe', 'Gauge (mm²)', 'Type', 'Length (mm)', 'Note'
  ];
  const rows = buildCutList(state).map(row => [
    row.index,
    row.wireName,
    row.from.connector,
    row.from.pin,
    row.to.connector,
    row.to.pin,
    row.color,
    row.stripeColor,
    row.gauge,
    row.wireType,
    row.length,
    row.note
  ]);
  return toCsv([header, ...rows]);
}

/** Builds the bill of materials as a single CSV: connector line items followed by wire line items. */
export function buildBomCsv(state: AppState): string {
  const bom = buildBom(state);
  const header = ['Category', 'Description', 'Manufacturer', 'Part number', 'Quantity', 'Total length (mm)'];
  const connectorRows = bom.connectors.map(row => [
    'Connector', row.name, row.manufacturer, row.partNumber, row.quantity, undefined
  ]);
  const wireRows = bom.wires.map(row => [
    'Wire', wireDescription(row), undefined, undefined, row.count, row.totalLength
  ]);
  return toCsv([header, ...connectorRows, ...wireRows]);
}
